import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useUpsertBrandingText, useBranding } from "@/hooks/useBranding";
import type { BrandingTextKey } from "@/lib/branding-keys";

interface Props {
  brandingKey: BrandingTextKey;
  label: string;
  placeholder?: string;
  multiline?: boolean;
  type?: "text" | "email" | "tel" | "url";
  description?: string;
}

/**
 * Self-contained editor for a single `site_branding.value_text` key.
 * Reads current value from the shared useBranding() cache, renders an input,
 * and persists on "Save" (no auto-save — non-designer owners want a clear
 * commit moment to avoid accidental overwrites).
 */
export function BrandingField({ brandingKey, label, placeholder, multiline, type = "text", description }: Props) {
  const { getText, isLoading } = useBranding();
  const upsert = useUpsertBrandingText();

  const serverValue = getText(brandingKey);
  const [value, setValue] = useState(serverValue);

  // Keep local state in sync when the query resolves or another tab saves.
  useEffect(() => {
    setValue(serverValue);
  }, [serverValue]);

  const dirty = value !== serverValue;

  const save = () => {
    upsert.mutate(
      { key: brandingKey, value },
      {
        onSuccess: () => toast.success(`Saved: ${label}`),
        onError: (e) => toast.error((e as Error).message || "Save failed"),
      }
    );
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor={brandingKey} className="text-sm font-medium">{label}</Label>
        {dirty && (
          <Button size="sm" variant="outline" onClick={save} disabled={upsert.isPending}>
            {upsert.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
        )}
      </div>
      {multiline ? (
        <Textarea
          id={brandingKey}
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          disabled={isLoading}
        />
      ) : (
        <Input
          id={brandingKey}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          disabled={isLoading}
        />
      )}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}
